import type { ShippingInfo } from '@/components/ShippingInfoForm';
import * as parsedOrderRepo from './parsedOrderRepository';

const toShippingInfo = (record: parsedOrderRepo.ParsedOrderRecord): Partial<ShippingInfo> => ({
  nickname: record.nickname,
  customer_name: record.name,
  customer_phone: record.phone_number,
  customer_address: record.address,
  delivery_notes: record.delivery_notes,
  productName: record.product_name,
});

/**
 * 특정 고객의 배송 정보를 조회합니다.
 * @returns 조회된 배송 정보 또는 null
 */
export const getShippingInfoByOrderId = async (orderId: number): Promise<Partial<ShippingInfo> | null> => {
  const record = await parsedOrderRepo.findByOrderId(orderId);

  if (!record) {
    return null;
  }

  return toShippingInfo(record);
};

/**
 * 동일 닉네임의 가장 최신 parsed_order를 조회합니다. (order_date DESC, created_at DESC)
 */
export const getLatestShippingInfoByNickname = async (
  userId: string,
  nickname: string
): Promise<Partial<ShippingInfo> | null> => {
  const record = await parsedOrderRepo.findLatestByUserAndNickname(userId, nickname);

  if (!record) return null;

  return toShippingInfo(record);
};

/**
 * order_id를 기준으로 배송 정보를 생성하거나 업데이트합니다.
 * order_date는 UI 상태가 아닌 order 테이블의 실제 값을 호출자가 전달해야 합니다.
 * @returns 실제 저장된 행 — upsert가 빈 productName을 보존하므로 제출 폼 데이터와 다를 수 있다.
 *          캐시 갱신은 반드시 이 반환값으로 할 것 (제출값 캐시 = 캐시-DB 괴리).
 */
export const saveShippingInfoByOrderId = async (
  orderId: number,
  userId: string,
  orderDate: string,
  shippingInfo: ShippingInfo
): Promise<Partial<ShippingInfo>> => {
  const record = await parsedOrderRepo.upsertByOrderId(orderId, userId, orderDate, shippingInfo);
  return toShippingInfo(record);
};

// 화면 진입 시 이미 조회하는 배송정보를 클릭 시 재사용한다.
// complete=false면 호출부는 "응답에 없음"을 부재로 단정하면 안 된다(플랜 §A).
export interface ShippingInfoBatchResult {
  byOrderId: Map<number, Partial<ShippingInfo>>;
  complete: boolean;
}

export const getShippingInfoByOrderIds = async (
  orderIds: number[]
): Promise<ShippingInfoBatchResult> => {
  if (!orderIds || orderIds.length === 0) return { byOrderId: new Map(), complete: true };
  const { rows, complete } = await parsedOrderRepo.findByOrderIds(orderIds);
  const byOrderId = new Map<number, Partial<ShippingInfo>>();
  rows.forEach((row) => {
    if (
      typeof row.order_id === 'number' &&
      !Number.isNaN(row.order_id) &&
      !byOrderId.has(row.order_id)
    ) {
      byOrderId.set(row.order_id, toShippingInfo(row));
    }
  });
  return { byOrderId, complete };
};

// 새로 추가: 주문 날짜 변경 시 parsed_order도 동일 날짜로 변경 (단건)
export const updateParsedOrderDateByOrderId = async (orderId: number, orderDate: string): Promise<boolean> => {
  const existing = await parsedOrderRepo.findByOrderId(orderId);
  if (!existing) return false;
  await parsedOrderRepo.patchOrderDateByOrderId(orderId, orderDate);
  return true;
};

// 새로 추가: 여러 주문에 대해 parsed_order 날짜 일괄 변경. affected count 반환.
export const updateParsedOrderDatesByOrderIds = async (orderIds: number[], orderDate: string): Promise<number> => {
  return await parsedOrderRepo.patchOrderDateByOrderIds(orderIds, orderDate);
};

// 특정 시각(created_at) 이후 자동정리 사용 이력이 있는지 확인합니다.
export const hasParsedOrdersSince = async (userId: string, sinceIso: string): Promise<boolean> => {
  return await parsedOrderRepo.existsParsedOrderSince(userId, sinceIso);
};

// 배송정보 상품명만 부분 갱신 (stale 스냅샷 원클릭 반영용).
// born-empty 가드: 빈/공백 문자열로는 기존 값을 절대 덮지 않는다 (product_name 민감 컬럼).
export const updateProductNameByOrderId = async (orderId: number, productName: string): Promise<void> => {
  if (!productName || !productName.trim()) return;
  await parsedOrderRepo.patchProductNameByOrderId(orderId, productName);
};

// ── 배송정보 상품명 stale 일괄 반영 (훅은 service만 import — 규율) ─────────────

export type {
  ShippingSyncCandidateRow,
  ShippingSyncApplyRow,
  ShippingSyncApplyStatus,
  ShippingSyncApplyResult,
} from './parsedOrderRepository';

/** 선택 날짜의 stale 스캔 후보 조회. verified=false = 스캔 실패(잘린 데이터 제안 금지). */
export const getShippingSyncCandidatesByDate = async (
  userId: string,
  orderDate: string
): Promise<{ rows: parsedOrderRepo.ShippingSyncCandidateRow[]; verified: boolean }> => {
  return parsedOrderRepo.findShippingSyncCandidatesByDate(userId, orderDate);
};

/** 배치 CAS 반영. 실패는 commit-ambiguous — 재스캔으로 처리(블라인드 재시도 금지). */
export const applyShippingProductNameSync = async (
  rows: parsedOrderRepo.ShippingSyncApplyRow[]
): Promise<parsedOrderRepo.ShippingSyncApplyResult[]> => {
  return parsedOrderRepo.applyShippingProductNameSync(rows);
};
